/**
 * ToastNotification — floating toast stack rendered as a global overlay.
 *
 * Mounted once in App.tsx as a sibling of all screens so it appears on top of
 * everything (zIndex 9000 — above shop, pack animation, push modal).
 *
 * WHY this component is separated from useToastStore:
 * The store is the source of truth; this component is pure presentation. Any future
 * redesign (e.g. slide-in from left on mobile) only touches this file.
 *
 * Slide-in animation uses a CSS keyframe injected via <style>. Framer Motion / GSAP
 * are NOT imported here to keep bundle size down — the game engine already has Phaser.
 *
 * Each toast auto-removes itself after its durationMs via the store timer. The user
 * can also tap/click a toast to dismiss it immediately.
 */

import React from 'react'
import { useToastStore } from './useToastStore.js'
import type { ToastVariant } from './useToastStore.js'

/** Per-variant colour palette. */
const VARIANT_STYLES: Record<ToastVariant, React.CSSProperties> = {
  info: { background: '#1e1e3a', border: '1px solid #3a3a6a', color: '#ccc' },
  success: { background: '#0f2a1a', border: '1px solid #2a6a3a', color: '#7edd9a' },
  warning: { background: '#2a1a00', border: '1px solid #6a4a00', color: '#ffb74d' },
  error: { background: '#2a0f0f', border: '1px solid #6a2a2a', color: '#e57373' },
  achievement: { background: '#1a1030', border: '1px solid #9B6FE8', color: '#c3a3f7' },
}

export const ToastNotification: React.FC = () => {
  const toasts = useToastStore((s) => s.toasts)
  const removeToast = useToastStore((s) => s.removeToast)

  if (toasts.length === 0) return null

  return (
    <>
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
      {/* Stack grows from bottom-right corner, stacks upward */}
      <div style={styles.stack}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            style={{ ...styles.toast, ...VARIANT_STYLES[toast.variant] }}
            onClick={() => removeToast(toast.id)}
            role="alert"
            aria-live="polite"
          >
            {toast.icon && <span style={styles.icon}>{toast.icon}</span>}
            <span style={styles.message}>{toast.message}</span>
          </div>
        ))}
      </div>
    </>
  )
}

const styles: Record<string, React.CSSProperties> = {
  stack: {
    position: 'fixed',
    bottom: 'max(env(safe-area-inset-bottom, 0px), 24px)',
    right: 16,
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: 8,
    zIndex: 9000,
    pointerEvents: 'all',
    maxWidth: 'min(360px, calc(100vw - 32px))',
  },
  toast: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    borderRadius: 12,
    backdropFilter: 'blur(8px)',
    cursor: 'pointer',
    animation: 'toastSlideIn 0.25s ease forwards',
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
    userSelect: 'none',
  },
  icon: {
    fontSize: 20,
    flexShrink: 0,
    lineHeight: 1,
  },
  message: {
    fontSize: 14,
    fontFamily: 'monospace',
    lineHeight: 1.4,
  },
}
